import { makeStateKey, StateKey, TransferState } from "@angular/core";
import { FastifyRequest as Request } from "fastify";

import {
    envConfigFactory,
    EnvironmentConfiguration,
    InfraSettings,
} from "@hermes/core-env-infra";

export const envConfigStateKey: StateKey<EnvironmentConfiguration> =
    makeStateKey("envConfig");

// Custom header set by the Cloudflare Worker upstream to forward the original host
const X_ORIGINAL_HOST_HEADER = "x-original-host";

export const httpTransferCacheOriginMapFactory = (
    infraSettings: InfraSettings,
) => ({
    // backendSSR
    [infraSettings.backendSSR as string]: infraSettings.backendCSR,
    // On INTx, there is no Gateway and settings are the same so don't add it twice on map
    ...(infraSettings.backendSSR === infraSettings.hcomGatewaySSR
        ? {}
        : {
              // On CSR with HcomGateway enabled and if different from backendSSR, calls are made through public URL with /api/xx
              [infraSettings.hcomGatewaySSR]: infraSettings.frontend,
          }),
});

export const browserEnvConfig = (
    transferState: TransferState,
    environment: {
        RDR_BACKEND_HOSTS_FROM_BUILD_ENV: string;
    },
): EnvironmentConfiguration =>
    transferState.get<EnvironmentConfiguration>(
        envConfigStateKey,
        envConfigFactory(environment),
    );

export const serverEnvConfig = (
    transferCache: TransferState,
    request: Request,
    environmentConfigFromSSR?: EnvironmentConfiguration,
): EnvironmentConfiguration => {
    if (!environmentConfigFromSSR) {
        throw new Error("environment config not provided");
    }

    transferCache.set<EnvironmentConfiguration>(
        envConfigStateKey,
        toBrowserEnvironmentConfig(request, environmentConfigFromSSR),
    );

    return environmentConfigFromSSR;
};

function toBrowserEnvironmentConfig(
    request: Request,
    environmentConfig: EnvironmentConfiguration,
): EnvironmentConfiguration {
    const currentHost =
        (request.headers[X_ORIGINAL_HOST_HEADER] as string) ?? request.host;

    const relevantHostConfig = environmentConfig.hosts
        ? environmentConfig.hosts[currentHost]
        : undefined;

    if (!relevantHostConfig) {
        throw new Error(
            `No environment configuration found for host: ${currentHost}`,
        );
    }

    return {
        hosts: {
            [currentHost]: {
                ...relevantHostConfig,
                backendSSR: undefined,
                hcomGatewaySSR: "",
                sensorsdata: undefined,
                datadomeKey: undefined,
            },
        },
    };
}
